import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { sleepTimer } from '../../global-state/audio-signals';
import { Icon } from '../icon';

const sleepTimerOptions = [
  { label: 'Sleep Timer Off', value: '' },
  { label: 'Sleep at End of Episode', value: 'end-of-episode' },
  { label: 'Sleep in 5 min', value: '5' },
  { label: 'Sleep in 10 min', value: '10' },
  { label: 'Sleep in 15 min', value: '15' },
  { label: 'Sleep in 30 min', value: '30' },
  { label: 'Sleep in 60 min', value: '60' },
] as const;

const SleepTimerSelect = () => {
  const [isOpen, setOpen] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const currentValue = sleepTimer.value;
  const hasActiveSleepTimer = currentValue !== '';
  const selectedValue =
    currentValue === ''
      ? ''
      : currentValue === 'end-of-episode'
        ? 'end-of-episode'
        : toClosestMinuteOption(currentValue);

  useLayoutEffect(() => {
    if (isOpen && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof currentValue !== 'number') return;
    const interval = window.setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(interval);
  }, [currentValue]);

  const onInput = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value;
    if (value === '' || value === 'end-of-episode') {
      sleepTimer.value = value;
      setOpen(false);
      return;
    }
    const minutes = parseInt(value, 10);
    sleepTimer.value = Date.now() + minutes * 60 * 1000;
    setOpen(false);
  };

  const remainingText = getSleepTimerRemainingText(currentValue, nowMs);

  return (
    <div class="relative flex flex-col items-center">
      <button
        class="button button--circle-icon h-9 w-9 bg-gray-100 p-2"
        style={{ '--button-height': '36px' }}
        onClick={() => setOpen(!isOpen)}
        aria-label="Set Sleep Timer"
      >
        <Icon sprite="moon-zzzs" class="-mt-1 h-7 w-7" />
      </button>
      {hasActiveSleepTimer && (
        <div
          class="absolute -bottom-1 whitespace-nowrap text-[0.65rem] leading-none text-gray-600"
          title="Sleep timer remaining"
        >
          {remainingText}
        </div>
      )}
      {isOpen && (
        <label class="absolute right-0 top-[calc(100%+6px)] z-10 rounded-[10px] border border-gray-300 bg-white p-1.5 shadow-[0_6px_14px_rgba(0,0,0,0.12)]">
          <span class="hidden">Set Sleep Timer</span>
          <select
            ref={selectRef}
            class="h-9 rounded-full border border-gray-300 bg-[#f8f8f8] px-2.5 text-sm"
            value={selectedValue}
            onInput={onInput}
            onBlur={() => setOpen(false)}
          >
            {sleepTimerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
};

function toClosestMinuteOption(timestampMs: number) {
  const remainingMinutes = Math.max(0, Math.round((timestampMs - Date.now()) / 60000));
  if (remainingMinutes === 0) return '';
  const numericOptions = sleepTimerOptions
    .map((opt) => parseInt(opt.value, 10))
    .filter((val) => Number.isFinite(val) && val > 0) as number[];
  const closest = numericOptions.reduce((closestValue, nextValue) => {
    return Math.abs(nextValue - remainingMinutes) <
      Math.abs(closestValue - remainingMinutes)
      ? nextValue
      : closestValue;
  });
  return `${closest}`;
}

function getSleepTimerRemainingText(value: typeof sleepTimer.value, nowMs: number) {
  if (value === 'end-of-episode') return 'End of episode';
  if (typeof value !== 'number') return '';
  const remainingMs = value - nowMs;
  if (remainingMs <= 60 * 1000) return '< 1 min';
  return `${Math.floor(remainingMs / 60000)} min`;
}

export default SleepTimerSelect;
